import { useState } from 'react';
import { importarLancamentos } from '../api';
import { Alert } from './common';
import { fmtMoeda, fmtData } from '../lib/format';

// Modelos de cabeçalho aceitos pelo importador (backend/src/escrituracao/importador.js).
const MODELO_PARTIDAS =
  'lancamento;data;historico;conta;dc;valor;complemento\n'
  + '1;15/09/2026;Pagamento a fornecedor por Pix;2.1.02.01.0001;D;1.500,00;\n'
  + '1;15/09/2026;Pagamento a fornecedor por Pix;1.1.01.02.0001;C;1.500,00;\n';
const MODELO_SIMPLES =
  'data;historico;debito;credito;valor\n'
  + '15/09/2026;Pagamento a fornecedor por Pix;2.1.02.01.0001;1.1.01.02.0001;1.500,00\n';

function baixar(nome, texto) {
  const url = URL.createObjectURL(new Blob(['﻿' + texto], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = nome; a.click();
  URL.revokeObjectURL(url);
}

/** Lê o arquivo como UTF-8; se vier com caractere inválido (CSV do Excel), relê como Windows-1252. */
function lerArquivo(file) {
  const ler = (enc) => new Promise((ok, falha) => {
    const fr = new FileReader();
    fr.onload = () => ok(String(fr.result));
    fr.onerror = () => falha(new Error('Não foi possível ler o arquivo.'));
    fr.readAsText(file, enc);
  });
  return ler('utf-8').then(t => (t.includes('�') ? ler('windows-1252') : t));
}

/**
 * Importa lançamentos de um CSV: simula primeiro (nada é gravado), mostra cada
 * lançamento com erros e avisos, e só grava quando o arquivo inteiro está limpo.
 */
export default function ImportadorLancamentos({ clienteId, onConcluido, onFechar }) {
  const [nome, setNome] = useState('');
  const [csv, setCsv] = useState('');
  const [sim, setSim] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);

  const simular = async (texto) => {
    setOcupado(true); setErro(null); setSim(null);
    try {
      setSim(await importarLancamentos(clienteId, texto, 'simular'));
    } catch (e) {
      setErro(e.message);
    } finally {
      setOcupado(false);
    }
  };

  const escolher = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNome(file.name);
    try {
      const texto = await lerArquivo(file);
      setCsv(texto);
      await simular(texto);
    } catch (err) {
      setErro(err.message);
    }
    e.target.value = '';
  };

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      const r = await importarLancamentos(clienteId, csv, 'gravar');
      onConcluido?.(r);
      setSim(null); setCsv(''); setNome('');
    } catch (e) {
      setErro(e.message);
    } finally {
      setOcupado(false);
    }
  };

  const r = sim?.resumo;
  const podeGravar = r && r.com_erro === 0 && r.a_gravar > 0 && !ocupado;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <strong className="card__title">Importar lançamentos (CSV)</strong>
        <span className="toolbar__spacer" />
        <span className="small muted">
          Modelo:{' '}
          <a href="#" onClick={e => { e.preventDefault(); baixar('modelo-lancamentos-partidas.csv', MODELO_PARTIDAS); }}>partidas</a>
          {' · '}
          <a href="#" onClick={e => { e.preventDefault(); baixar('modelo-lancamentos-simples.csv', MODELO_SIMPLES); }}>simples</a>
          {onFechar && <> {' · '}<a href="#" onClick={e => { e.preventDefault(); onFechar(); }}>fechar</a></>}
        </span>
      </div>
      <p className="small muted" style={{ marginTop: 0 }}>
        A conta é o código do plano deste cliente. Primeiro o arquivo é simulado e nada é gravado;
        a gravação só libera quando nenhum lançamento tem erro, e entra tudo ou nada.
        Reimportar o mesmo arquivo não duplica.
      </p>

      <input type="file" accept=".csv,.txt,text/csv" onChange={escolher} disabled={ocupado}
        aria-label="Arquivo CSV de lançamentos" />
      {nome && <span className="small muted" style={{ marginLeft: 8 }}>{nome}</span>}
      {ocupado && <span className="small muted" style={{ marginLeft: 8 }}>processando…</span>}

      {erro && <Alert tipo="danger">{erro}</Alert>}

      {r && (
        <>
          <div className="small" style={{ margin: '12px 0' }}>
            <strong>{r.total}</strong> lançamento(s) · <strong>{r.a_gravar}</strong> a gravar
            {r.ja_importados > 0 && <> · {r.ja_importados} já importado(s)</>}
            {r.com_erro > 0 && <> · <span style={{ color: 'var(--danger, #c45a5a)' }}>{r.com_erro} com erro</span></>}
            {' · '}total {fmtMoeda(r.valor_total)}
          </div>

          <table className="data">
            <thead>
              <tr><th>#</th><th>Data</th><th>Histórico</th><th className="num">Valor</th><th>Situação</th></tr>
            </thead>
            <tbody>
              {sim.lancamentos.map((l, i) => (
                <tr key={i} onClick={() => setAberto(aberto === i ? null : i)} style={{ cursor: 'pointer', verticalAlign: 'top' }}>
                  <td>{l.chave}</td>
                  <td>{l.data_lcto ? fmtData(l.data_lcto) : '?'}</td>
                  <td>
                    {l.historico}
                    {aberto === i && (
                      <div className="small muted" style={{ marginTop: 4 }}>
                        {l.partidas.map((p, j) => (
                          <div key={j}>{p.lado} {p.conta} {p.conta_nome || ''} · {fmtMoeda(p.valor || 0)}{p.historico_compl ? ` · ${p.historico_compl}` : ''}</div>
                        ))}
                      </div>
                    )}
                    {l.avisos.map((a, j) => <div key={j} className="small" style={{ color: 'var(--warning, #c4956a)' }}>{a}</div>)}
                  </td>
                  <td className="num">{fmtMoeda(l.total_debito || 0)}</td>
                  <td className="small">
                    {l.erros.length
                      ? l.erros.map((e, j) => <div key={j} style={{ color: 'var(--danger, #c45a5a)' }}>{e}</div>)
                      : l.ja_importado ? 'já importado' : 'ok'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="toolbar" style={{ marginTop: 12 }}>
            <span className="small muted">
              {r.com_erro > 0 ? 'Corrija a planilha e escolha o arquivo de novo.' : 'Confira antes de gravar.'}
            </span>
            <span className="toolbar__spacer" />
            <button className="primary" onClick={gravar} disabled={!podeGravar}>
              Gravar {r.a_gravar} lançamento(s)
            </button>
          </div>
        </>
      )}
    </div>
  );
}
